<template>
  <AltAvatar :name="name" :size="size" :src="src" :title="name" />
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { AltAvatar } from 'altoneo-charting';
import { avatarSrc } from '@/lib/avatar';

/**
 * Picto d'un collaborateur — photo si elle existe, initiales colorées sinon.
 *
 * POURQUOI CE COMPOSANT. `AltAvatar` n'affiche une photo que si on lui passe
 * `src`, et il ne sait pas la calculer. Résultat : sur treize emplacements, dix
 * appelaient `AltAvatar` avec le seul nom et montraient donc les initiales même
 * quand la photo existait. La règle « photo si disponible » ne doit pas se
 * recopier à la main partout — sinon le prochain écran l'oubliera à son tour.
 *
 * `hasAvatar` est EXPLICITE et non deviné : sans lui on tenterait une URL qui
 * répond 404, et le navigateur afficherait une icône d'image cassée, pire que
 * les initiales. C'est l'API qui sait, elle le dit.
 *
 * Même alors, la photo est PRÉCHARGÉE et ne s'affiche qu'une fois arrivée
 * (DEC-037) : un compte Microsoft peut n'avoir posé aucune photo, et l'on garde
 * ses initiales plutôt qu'une image cassée.
 */
const props = withDefaults(
  defineProps<{
    /** Identifiant du collaborateur ; absent = pas de photo possible (externe, système). */
    id?: string | null;
    name: string;
    /** L'API a-t-elle une photo pour lui ? */
    hasAvatar?: boolean | null;
    /**
     * URL déjà calculée, pour les cas où il n'y a PAS d'identifiant : un
     * collaborateur de l'annuaire qui n'a pas encore de compte (0046). Elle
     * prime sur `id`, mais reste soumise à `hasAvatar` — la règle « on ne
     * tente pas une URL qui répondra 404 » ne change pas.
     */
    url?: string | null;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  }>(),
  { id: null, hasAvatar: false, url: null, size: 'sm' },
);

const candidate = computed(() => {
  if (!props.hasAvatar) return undefined;
  if (props.url) return props.url;
  return props.id ? avatarSrc(props.id) : undefined;
});

const src = ref<string | undefined>(undefined);
watch(
  candidate,
  (url) => {
    src.value = undefined;
    if (!url) return;
    const img = new Image();
    img.onload = () => {
      if (candidate.value === url) src.value = url;
    };
    img.src = url;
  },
  { immediate: true },
);
</script>
